import i18n from '../i18n'
import { errorCode } from '../services/api'

/**
 * Direct debit (design page 17, `autopay`; P-autopay of the go-live plan).
 * Mirrored from the Android app's `AutopayScreen.kt`/`strings.xml` so both
 * clients offer the same four banks and three ceilings, worded the same.
 *
 * A plain lib file, not a component, so it reads `i18n.t()` directly — the
 * `autopay` namespace. `BANKS`' names are getters so a language switch is
 * picked up without re-importing; `AMOUNT` became the function `amount()`
 * for the same reason (a static string can't react to a language change).
 */

const t = (key, options) => i18n.t(key, { ns: 'autopay', ...options })

export const BANKS = [
  {
    id: 'melli',
    get chip() { return t('banks.melli.chip') },
    get full() { return t('banks.melli.full') },
  },
  {
    id: 'saman',
    get chip() { return t('banks.saman.chip') },
    get full() { return t('banks.saman.full') },
  },
  {
    id: 'pasargad',
    get chip() { return t('banks.pasargad.chip') },
    get full() { return t('banks.pasargad.full') },
  },
  {
    id: 'blu',
    get chip() { return t('banks.blu.chip') },
    get full() { return t('banks.blu.full') },
  },
]

export function bankById(id) {
  return BANKS.find((bank) => bank.id === id) ?? BANKS[0]
}

/** Thousands of toman — the design's three chips. */
export const CEILINGS = [150, 200, 500]

export const MAX_DAY = 28

/** The fixed monthly amount: the subscription's current price, stated in copy, same as the Android app's. */
export function amount() {
  return t('amount')
}

/** At least 24 digits after `IR`, whatever else was typed. */
export const IBAN_DIGITS = 24

export function ibanDigits(text) {
  return text.replace(/\D/g, '')
}

export function ibanComplete(text) {
  return ibanDigits(text).length >= IBAN_DIGITS
}

/** The last four digits, which is all the app keeps of the number. */
export function ibanTail(text) {
  const digits = ibanDigits(text)
  return digits.slice(-4)
}

/** The server's `monthlyCapMinor` (real money, minor units — Rial) as the design's thousands-of-toman chip. */
export function ceilingFromMinor(monthlyCapMinor) {
  const minor = Number(monthlyCapMinor ?? 2_000_000)
  return Math.round(minor / 10_000)
}

/** The chosen ceiling chip, as real money (Rial) for the request body. */
export function ceilingToMinor(ceiling) {
  return ceiling * 10_000
}

/** The server's status response, defaulted the way an inactive learner's form should start. */
export function toStatus(dto) {
  return {
    active: dto?.active ?? false,
    bank: dto?.bank ?? 'melli',
    ibanTail: dto?.ibanTail ?? '',
    ceiling: ceilingFromMinor(dto?.monthlyCapMinor),
    day: dto?.withdrawalDay ?? 5,
    remind: dto?.remindEnabled ?? true,
    enabled: dto?.enabled ?? false,
  }
}

/**
 * The sentence for why a mandate action did not go through. A request that
 * never reached the server is offline, not a refusal — same rule as the
 * shop's.
 */
export function refusalFor(error) {
  if (!error?.response) {
    return { good: false, text: t('refusal.offline') }
  }
  if (errorCode(error) === 'autopay_not_enabled') {
    return { good: false, text: t('refusal.notEnabled') }
  }
  return { good: false, text: t('refusal.default') }
}
